'use client'

import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { pushToGtmDataLayer } from '@/lib/tracking/services/tracking.gtm'
import { AccordionItem } from '@/ui/accordion/AccordionItem'
import type {
  ProductListFilterFragment,
  ProductListFilterValueFilterFragment,
} from '@gql-generated/graphql'
import { useMemo, type RefAttributes } from 'react'
import { ProductListFilter } from '../product-list-filter/ProductListFilter.client'

type ProductListFiltersProductProps = {
  productFilters: ProductListFilterFragment
  isMobile?: boolean
} & RefAttributes<HTMLDivElement>

export function ProductListFiltersProduct({
  ref,
  productFilters,
  isMobile = false,
}: ProductListFiltersProductProps) {
  const { t } = useTranslation(['productListFiltersProduct'])

  const { title, values: filters } = productFilters

  const isSearchable = title === t('productListFiltersProduct:sections.brand.title')
  const displayTitle =
    (isSearchable && t('productListFiltersProduct:sections.brand.accordionTitle')) || title

  const numberOfActiveFilters = useMemo(
    () => filters?.map((filter) => filter.isActive).filter(Boolean).length,
    [filters],
  )

  if (!filters || filters.length === 0) return null

  return (
    <AccordionItem
      id={title}
      ref={ref}
      onExpandedChange={() =>
        pushToGtmDataLayer({
          event: 'gaEvent',
          eventCategory: 'filtres',
          eventAction: 'ouverture bloc',
          eventLabel: title,
        })
      }
      className="w-full"
    >
      <AccordionItem.Header
        title={displayTitle}
        notificationCount={
          isMobile && numberOfActiveFilters && numberOfActiveFilters > 0
            ? numberOfActiveFilters
            : undefined
        }
      />
      <AccordionItem.Panel>
        <ProductListFilter
          title={displayTitle}
          filters={filters as ProductListFilterValueFilterFragment[]} // Fix for incorrect type generation in codegen
          collapsedViewItemCount={5}
          isSearchable={isSearchable}
          searchInputPlaceholder={
            isSearchable ? t('productListFiltersProduct:sections.brand.placeholder') : undefined
          }
          isMobile={isMobile}
        />
      </AccordionItem.Panel>
    </AccordionItem>
  )
}
